/**
 * Editar um plano (docs/spec/planos.md, "Editar os planos"): o nome em português e em inglês, os quatro
 * preços (o Pix em real e o cartão em dólar, por mês e por ano) e o que o plano dá. O diálogo confere na hora o que o servidor confere ao gravar (a faixa de
 * cada valor, o aninhamento com os outros planos) e avisa do teto do servidor e de quantas contas
 * passariam do espaço novo. Nada é apagado: quem passar só não envia arquivos novos.
 */
import { useEffect, useState } from 'react';
import { api } from '../api.js';
import { formatBytes } from '../bytes.js';
import { translate, useLanguage, useT } from '../i18n/index.js';
import {
  DEFAULT_PLAN,
  ENTITLEMENT_INFO,
  PLAN_CONFIG_LIMITS,
  PRICE_KEYS,
  entitlementProblem,
  nestingProblem,
  samePlanConfig,
  type AdminPlanRow,
  type EntitlementKey,
  type Entitlements,
  type PlanCeilings,
  type PlanConfig,
  type PlanGrid,
  type PlanPrices,
  type PriceKey,
} from '../shared.js';
import { ENTITLEMENT_KEYS, SIZE_UNITS, parsePrice, parseSize, planName, priceInput, sizeInput, type SizeUnit } from '../plans/planModel.js';
import { describeError, useApp } from '../store.js';
import { Button, ComboBox, Dialog, TextEdit } from '../ui/controls.js';
import { toast } from '../ui/Toaster.js';

type SizeField = { value: string; unit: SizeUnit };

export function AdminPlanEditDialog({ row, plans, ceilings, onDone }: { row: AdminPlanRow; plans: readonly AdminPlanRow[]; ceilings: PlanCeilings; onDone: (saved: boolean) => void }) {
  const t = useT();
  const language = useLanguage((s) => s.language);
  const free = row.id === DEFAULT_PLAN;
  const [namePt, setNamePt] = useState(row.config.names.pt ?? '');
  const [nameEn, setNameEn] = useState(row.config.names.en ?? '');
  const [prices, setPrices] = useState<Record<PriceKey, string>>(() => Object.fromEntries(PRICE_KEYS.map((key) => [key, priceInput(row.config.prices[key], language)])) as Record<PriceKey, string>);
  const [sizes, setSizes] = useState<Record<EntitlementKey, SizeField>>(
    () => Object.fromEntries(ENTITLEMENT_KEYS.map((key) => [key, sizeInput(row.config.entitlements[key], language)])) as Record<EntitlementKey, SizeField>,
  );
  const [impact, setImpact] = useState<{ storageBytes: number; over: number } | null>(null);
  const [busy, setBusy] = useState(false);

  // o que foi digitado, em bytes e centavos (null: não é um número; um preço vazio é sem preço, e
  // undefined é um preço que não vale)
  const bytes = Object.fromEntries(ENTITLEMENT_KEYS.map((key) => [key, parseSize(sizes[key].value, sizes[key].unit)])) as Record<EntitlementKey, number | null>;
  const cents = Object.fromEntries(PRICE_KEYS.map((key) => [key, free ? null : parsePrice(prices[key])])) as Record<PriceKey, number | null | undefined>;
  const badPrices = PRICE_KEYS.filter((key) => cents[key] === undefined || cents[key]! > PLAN_CONFIG_LIMITS.maxPriceCents);
  const invalid = ENTITLEMENT_KEYS.filter((key) => bytes[key] === null || entitlementProblem(key, bytes[key]));
  const entitlements = invalid.length ? null : (bytes as Entitlements);
  const draft: PlanConfig | null = entitlements && !badPrices.length ? { entitlements, names: { pt: namePt, en: nameEn }, prices: cents as PlanPrices } : null;
  const grid = Object.fromEntries(plans.map((p) => [p.id, p.id === row.id && entitlements ? entitlements : p.config.entitlements])) as PlanGrid;
  const nesting = entitlements ? nestingProblem(grid) : null;
  const unchanged = draft ? samePlanConfig(draft, row.config) : false;
  const nameOf = (id: string) => planName(t, id, plans.find((p) => p.id === id)?.config.names, language);

  // quantas contas do plano passariam do espaço novo (só quando ele diminui)
  const storage = entitlements?.storageBytes ?? null;
  useEffect(() => {
    const token = useApp.getState().token;
    if (!token || storage === null || storage >= row.config.entitlements.storageBytes) return;
    let alive = true;
    const timer = window.setTimeout(() => {
      api.adminPlanImpact(token, row.id, storage).then(
        (result) => {
          if (alive) setImpact({ storageBytes: storage, over: result.over });
        },
        () => undefined,
      );
    }, 300);
    return () => {
      alive = false;
      window.clearTimeout(timer);
    };
  }, [storage, row.id, row.config.entitlements.storageBytes]);
  const over = impact && storage !== null && impact.storageBytes === storage && storage < row.config.entitlements.storageBytes ? impact.over : 0;

  const submit = async () => {
    const token = useApp.getState().token;
    if (!token || !draft || busy) return;
    setBusy(true);
    try {
      await api.adminSavePlanConfig(token, row.id, draft);
      toast(t('admin.plans.saved', { plan: planName(t, row.id, draft.names, language) }), 'success');
      onDone(true);
    } catch (error) {
      toast(describeError(error), 'error');
      setBusy(false);
    }
  };

  const ceiling = ceilings.maxFileBytes;
  return (
    <Dialog
      title={t('admin.plans.editTitle', { plan: nameOf(row.id) })}
      onClose={() => onDone(false)}
      actions={
        <>
          <Button variant="secondary" onClick={() => onDone(false)}>
            {t('create.cancel')}
          </Button>
          <Button variant="action" busy={busy} disabled={!draft || !!nesting || unchanged} onClick={() => void submit()}>
            {t('admin.plans.save')}
          </Button>
        </>
      }
    >
      <div className="rd-planDialog">
        {/* sem nome, vale o do i18n de cada língua: é o que a dica mostra */}
        <div className="rd-planDialog__row">
          <label className="rd-planDialog__field">
            <span>{t('admin.plans.namePt')}</span>
            <TextEdit look="filled" value={namePt} onChange={setNamePt} maxLength={PLAN_CONFIG_LIMITS.name} placeholder={translate('pt-BR', `plans.name.${row.id}`)} aria-label={t('admin.plans.namePt')} />
          </label>
          <label className="rd-planDialog__field">
            <span>{t('admin.plans.nameEn')}</span>
            <TextEdit look="filled" value={nameEn} onChange={setNameEn} maxLength={PLAN_CONFIG_LIMITS.name} placeholder={translate('en', `plans.name.${row.id}`)} aria-label={t('admin.plans.nameEn')} />
          </label>
        </div>
        {/* o Pix em real e o cartão em dólar, cada um por mês e por ano: uma linha por forma de pagamento */}
        {[PRICE_KEYS.slice(0, 2), PRICE_KEYS.slice(2)].map((pair) => (
          <div key={pair[0]} className="rd-planDialog__row">
            {pair.map((key) => (
              <label key={key} className="rd-planDialog__field">
                <span>{t(`admin.plans.priceLabel.${key}`)}</span>
                <TextEdit
                  look="filled"
                  value={free ? '' : prices[key]}
                  onChange={(value) => setPrices((current) => ({ ...current, [key]: value }))}
                  disabled={free}
                  invalid={badPrices.includes(key)}
                  inputMode="decimal"
                  placeholder={free ? t('admin.plans.freeNoPrice') : t('plans.noPrice')}
                  aria-label={t(`admin.plans.priceLabel.${key}`)}
                />
              </label>
            ))}
          </div>
        ))}
        {ENTITLEMENT_KEYS.map((key) => {
          const info = ENTITLEMENT_INFO[key];
          const bad = invalid.includes(key);
          return (
            <div key={key} className="rd-planDialog__field">
              <span id={`plan-${key}`}>{t(`plans.entitlement.${key}`)}</span>
              <div className="rd-planDialog__size">
                <TextEdit
                  look="filled"
                  value={sizes[key].value}
                  onChange={(value) => setSizes((current) => ({ ...current, [key]: { ...current[key], value } }))}
                  invalid={bad}
                  inputMode="decimal"
                  aria-labelledby={`plan-${key}`}
                />
                <ComboBox
                  value={sizes[key].unit}
                  onChange={(unit) => setSizes((current) => ({ ...current, [key]: { ...current[key], unit } }))}
                  aria-label={t('admin.plans.unit', { what: t(`plans.entitlement.${key}`) })}
                  options={SIZE_UNITS.map((unit) => ({ value: unit, label: unit }))}
                />
              </div>
              {bad ? (
                <p className="rd-planDialog__warning" role="alert">
                  {t('admin.plans.range', { min: formatBytes(info.min ?? 0), max: formatBytes(info.max ?? 0) })}
                </p>
              ) : null}
              {key === 'maxFileBytes' && ceiling !== undefined && bytes[key] !== null && bytes[key]! > ceiling ? (
                <p className="rd-planDialog__hint">{t('admin.plans.overCeiling', { ceiling: formatBytes(ceiling) })}</p>
              ) : null}
            </div>
          );
        })}
        {badPrices.length ? (
          <p className="rd-planDialog__warning" role="alert">
            {t('admin.plans.badPrice')}
          </p>
        ) : null}
        {nesting ? (
          <p className="rd-planDialog__warning" role="alert">
            {t('admin.plans.nesting', { what: t(`plans.entitlement.${nesting.key}`), plan: nameOf(nesting.plan), below: nameOf(nesting.below) })}
          </p>
        ) : null}
        {over > 0 ? (
          <p className="rd-planDialog__warning" role="alert">
            {t('admin.plans.impact', { n: over })}
          </p>
        ) : null}
        {unchanged ? (
          <p className="rd-planDialog__hint" role="status">
            {t('admin.plans.unchanged')}
          </p>
        ) : null}
      </div>
    </Dialog>
  );
}
